import { Injectable, Inject, LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import { PrimeNGConfig } from 'primeng/api';
import { BehaviorSubject } from 'rxjs';

import localeEn from '@angular/common/locales/en';
import localeEnGB from '@angular/common/locales/en-GB';

export type SupportedLocale = 'en-US' | 'en-GB';

export interface LocaleConfig {
    code: SupportedLocale;
    name: string;
    dateFormat: string;
    shortDateFormat: string;
    primeNgConfig: any;
}

@Injectable({
    providedIn: 'root'
})
export class LocaleService {
    private currentLocaleSubject = new BehaviorSubject<SupportedLocale>('en-US');
    public currentLocale$ = this.currentLocaleSubject.asObservable();

    private localeConfigs: Record<SupportedLocale, LocaleConfig> = {
        'en-US': {
            code: 'en-US',
            name: 'English (United States)',
            dateFormat: 'MM/dd/yyyy',
            shortDateFormat: 'M/d/yy',
            primeNgConfig: {
                firstDayOfWeek: 0, // Sunday
                dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
                dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
                monthNames: [
                    'January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'
                ],
                monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                today: 'Today',
                clear: 'Clear',
                dateFormat: 'mm/dd/yy',
                weekHeader: 'Wk',
                weak: 'Weak',
                medium: 'Medium',
                strong: 'Strong',
                passwordPrompt: 'Enter a password',
                emptyFilterMessage: 'No results found',
                emptyMessage: 'No available options'
            }
        },
        'en-GB': {
            code: 'en-GB',
            name: 'English (United Kingdom)',
            dateFormat: 'dd/MM/yyyy',
            shortDateFormat: 'd/M/yy',
            primeNgConfig: {
                firstDayOfWeek: 1, // Monday
                dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
                dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
                dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
                monthNames: [
                    'January', 'February', 'March', 'April', 'May', 'June',
                    'July', 'August', 'September', 'October', 'November', 'December'
                ],
                monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
                today: 'Today',
                clear: 'Clear',
                dateFormat: 'dd/mm/yy',
                weekHeader: 'Wk',
                weak: 'Weak',
                medium: 'Medium',
                strong: 'Strong',
                passwordPrompt: 'Enter a password',
                emptyFilterMessage: 'No results found',
                emptyMessage: 'No available options'
            }
        }
    };

    constructor(private primengConfig: PrimeNGConfig) {
        registerLocaleData(localeEn, 'en-US');
        registerLocaleData(localeEnGB, 'en-GB');

        this.setLocale('en-GB');
    }

    setLocale(locale: SupportedLocale): void {
        if (!this.localeConfigs[locale]) {
            console.warn(`Locale ${locale} is not supported. Falling back to en-GB.`);
            locale = 'en-GB';
        }

        this.currentLocaleSubject.next(locale);

        this.primengConfig.setTranslation(this.localeConfigs[locale].primeNgConfig);
    }

    getCurrentLocale(): SupportedLocale {
        return this.currentLocaleSubject.value;
    }

    getCurrentLocaleConfig(): LocaleConfig {
        return this.localeConfigs[this.getCurrentLocale()];
    }

    getAvailableLocales(): LocaleConfig[] {
        return Object.values(this.localeConfigs);
    }

    getDateFormat(): string {
        return this.getCurrentLocaleConfig().dateFormat;
    }

    getShortDateFormat(): string {
        return this.getCurrentLocaleConfig().shortDateFormat;
    }
}